<template>
    <div class="tw:w-full tw:xl:w-2xl">
        <form novalidate @submit.prevent="postLogEntry">
            <header class="modal-card-head">
                <p class="modal-card-title has-ellipsis" style="width: 100%">
                    {{ course.title }}
                </p>
            </header>

            <section id="course-log-container" class="modal-card-body">
                <div v-if="loading" class="m-b-xl">
                    <article class="media">
                        <figure class="media-left">
                            <p class="image is-48x48">
                                <e-skeleton circle width="48px" height="48px" />
                            </p>
                        </figure>
                        <div class="media-content">
                            <div class="content">
                                <p>
                                    <e-skeleton active />
                                    <e-skeleton active height="80px" />
                                </p>
                            </div>
                        </div>
                    </article>
                </div>

                <div v-else>
                    <div v-for="(logEntry, index) in logEntries" :key="logEntry.id" :class="logEntry.type">
                        <div :id="`log-entry-${logEntry.id}`" class="tw:flex tw:justify-between">
                            <div class="">
                                <div v-if="logEntry.type !== 'generated' && logEntry.creator" class="">
                                    <user-menu :user="logEntry.creator" />
                                </div>
                                <div v-if="logEntry.type === 'generated'" class="">
                                    <e-tooltip label="This message was generated automatically.">
                                        <e-tag>System</e-tag>
                                    </e-tooltip>
                                </div>
                            </div>
                            <div class="tw:flex tw:flex-col tw:xl:flex-row tw:items-end tw:xl:justify-between tw:xl:items-center tw:gap-1 tw:xl:gap-2">
                                <datetime-display :datetime="logEntry.created_at" calendar />
                                <confirm-button
                                    v-if="$gate.allows('delete', logEntry)"
                                    type="is-danger"
                                    size="is-small"
                                    :loading="removing === logEntry.id"
                                    @confirmed="remove(logEntry)"
                                />
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column" v-html="formatLogEntryDescription(logEntry)" />
                        </div>

                        <hr v-if="index !== logEntries.length - 1" />
                    </div>
                    <vue-eternal-loading v-if="logEntries.length === 0" :load="load">
                        <template #no-more>
                            <p class="has-text-subtle m-b-md">No log entries.</p>
                        </template>
                    </vue-eternal-loading>
                </div>
            </section>

            <footer
                v-if="$gate.allows('create', ['CourseLogEntry', course])"
                class="modal-card-foot p-b-none"
                style="justify-content: space-between; align-items: flex-start;"
            >
                <user-menu :user="authUser" :show-name="false" :show-dropdown="false" />
                <div style="flex-grow: 2" class="m-r-md">
                    <e-field :message="inputMessage">
                        <e-input
                            v-model="newLogEntry"
                            expanded
                            type="textarea"
                            maxlength="2000"
                            validation-message="test"
                            placeholder="Add a log entry..."
                            class="tw:w-full tw:px-2!"
                            @keydown.meta.enter="postLogEntry"
                            @keyup.ctrl.enter="postLogEntry"
                        />
                    </e-field>
                </div>
                <div class="tw:py-1">
                    <e-button
                        :loading="logging"
                        :disabled="!canLog || !newLogEntry"
                        type="is-primary"
                        native-type="submit"
                    >
                        <e-icon icon="message" />
                    </e-button>
                </div>
            </footer>
        </form>
    </div>
</template>

<script>
import { VueEternalLoading } from '@ts-pro/vue-eternal-loading';
import { htmlEscape } from 'escape-goat';
import last from 'lodash/last.js';
import map from 'lodash/map.js';
import { mapState } from 'vuex';

import ConfirmButton from '@/components/ConfirmButton.vue';
import DatetimeDisplay from '@/components/DatetimeDisplay.vue';
import UserMenu from '@/components/user-menu/UserMenu.vue';
import { handleResponseError } from '@/Helpers.js';
import Course from '@/models/Course.js';
import CourseLogEntry from '@/models/CourseLogEntry.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default {
    name: 'CourseLogEntries',

    components: { ConfirmButton, DatetimeDisplay, UserMenu, VueEternalLoading },

    props: {
        courseId: {
            type: Number,
            required: true,
        },
    },

    data: () => ({
        loading: false,
        pagination: {
            total: null,
            current_page: 1,
            per_page: 15,
            from: 1,
            ids: [],
            last_page: null,
        },
        logging: false,
        removing: null,

        newLogEntry: null,
        type: 'general',
    }),

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),

        course() {
            return Course.find(this.courseId);
        },

        logEntries() {
            return CourseLogEntry.query()
                .with(['loggable', 'creator', 'editor'])
                .where('course_id', this.courseId)
                .orderBy('created_at', 'asc')
                .get();
        },

        canLog() {
            return !!this.newLogEntry;
        },

        newLogEntryPayload() {
            return {
                course_id: this.courseId,
                type: this.type,
                description: this.newLogEntry,
            };
        },

        inputMessage() {
            if (!this.newLogEntry) {
                return '';
            }

            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

            return isMac ? '⌘⏎ to submit' : 'Ctrl + ⏎ to submit';
        },
    },

    async created() {
        await this.loadLogEntries();

        if (this.logEntries.length) {
            const lastEntryId = last(this.logEntries).id;

            this.$scrollTo(document.getElementById(`log-entry-${lastEntryId}`), 0, {
                container: '#course-log-container',
                cancelable: false,
            });
        }
    },

    methods: {
        formatLogEntryDescription(logEntry) {
            const { description } = logEntry;

            // Check if this is an unlock entry with expiration date and reason
            const unlockMatch = description.match(
                /^Course unlocked by (.+?) \(expires at (\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})\)(?:\s*\|\s*reason:\s*(.+))?$/
            );

            if (unlockMatch) {
                const [, userName, dateString, reason] = unlockMatch;
                // Format the date using moment with the app's standard format (matches DateFormat component)
                const formattedDate = this.$moment
                    .utc(dateString, 'YYYY-MM-DD HH:mm:ss')
                    .tz(this.$timezone.name)
                    .format('MM/DD/YYYY hh:mm:ss a zz');

                // Build the formatted description
                let formatted = `Course unlocked by ${htmlEscape(userName)} (expires at ${htmlEscape(formattedDate)})`;
                if (reason) {
                    formatted += `<br><span class="has-text-subtle">Reason: ${htmlEscape(reason.trim())}</span>`;
                }

                return formatted;
            }

            // Check if this is a lock entry with reason
            const lockMatch = description.match(/^Course locked by (.+?)(?:\s*\|\s*reason:\s*(.+))?$/);
            if (lockMatch) {
                const [, userName, reason] = lockMatch;
                let formatted = `Course locked by ${htmlEscape(userName)}`;
                if (reason) {
                    formatted += `<br><span class="has-text-subtle">Reason: ${htmlEscape(reason.trim())}</span>`;
                }
                return formatted;
            }

            // Return original description if no match (safely escaped to prevent XSS)
            return htmlEscape(description);
        },

        async load({ loaded, noMore }) {
            const response = await this.loadLogEntries(this.pagination.current_page + 1, true);

            if (response.data.data.length === 0) {
                noMore();
                return;
            }

            loaded(response.data.data.length, 15);
        },

        async loadLogEntries(page = 1, silently = false) {
            if (!silently) {
                this.loading = true;
            }

            const queryBuilder = new QueryBuilder('course-log-entries')
                .include(['creator', 'editor', 'loggable'])
                .filter('course_id', this.courseId)
                .sort('created_at', 'desc')
                .page(page);

            const { response } = await CourseLogEntry.api().get(queryBuilder.toUrl());

            this.pagination.current_page = response.data.meta.current_page;
            this.pagination.total = response.data.meta.total;
            this.pagination.per_page = response.data.meta.per_page;
            this.pagination.from = response.data.meta.from;
            this.pagination.ids = map(response.data.data, 'id');
            this.pagination.last_page = response.data.meta.last_page;

            if (!silently) {
                this.loading = false;
            }

            return response;
        },

        async postLogEntry() {
            if (!this.newLogEntry) {
                return;
            }

            this.logging = true;

            try {
                const { response } = await CourseLogEntry.api().post(
                    '/api/course-log-entries',
                    this.newLogEntryPayload,
                    {
                        errorHandle: false,
                    }
                );

                const newEntryId = response.data.data.id;

                this.pagination.ids.unshift(newEntryId);

                this.$nextTick(() => {
                    this.$scrollTo(document.getElementById(`log-entry-${newEntryId}`), 2000, {
                        container: '#course-log-container',
                        cancelable: false,
                    });
                });

                await this.course.$update({
                    log_entries_count: this.course.log_entries_count + 1,
                });
            } catch (error) {
                handleResponseError(error);
            }

            this.logging = false;
            this.newLogEntry = '';
        },

        async remove(logEntry) {
            this.removing = logEntry.id;

            try {
                await CourseLogEntry.api().delete(`/api/course-log-entries/${logEntry.id}`, { errorHandle: false });

                logEntry.$delete();

                await this.course.$update({
                    log_entries_count: this.course.log_entries_count - 1,
                });

                await this.loadLogEntries(this.pagination.current_page, true);
            } catch (e) {
                handleResponseError(e);
            }

            this.removing = null;
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.textarea:not([rows]) {
    min-height: 2.8em !important;
}

.generated {
    color: bulmaDarken($text-subtle, 50%);
}

.dark-mode {
    .generated {
        color: #ccc;
    }
}
</style>
